import React, { useState } from "react";
import { Mail, Phone, Send, MessageCircle, Anchor, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function Contatti() {
  const { lang, localizePath } = useI18n();
  const IT = lang === "it";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(IT ? "Nome, email e messaggio richiesti" : "Name, email and message required");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(IT ? "Messaggio inviato. Ti rispondiamo entro 24 ore." : "Message sent. We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(IT ? "Errore. Riprova." : "Error. Please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contatti-page" className="bg-white pt-24 pb-20">
      {/* HERO STRIPE */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-12 pb-10">
          <p className="label-eyebrow">{IT ? "Parliamone" : "Speak with a concierge"}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.05] max-w-3xl">
            {IT
              ? "Un consulente Seamatch è a un messaggio di distanza."
              : "Your Seamatch concierge is one message away."}
          </h1>
          <p className="mt-5 max-w-2xl text-ink leading-relaxed">
            {IT
              ? "Hai una richiesta particolare, una domanda sulla disponibilità o vuoi un consiglio su misura? Scrivici. Riceverai una risposta umana entro 24 ore."
              : "A bespoke itinerary, a specific yacht in mind, a quiet question for our team — share a few lines below. We typically return with thoughtful, personal answers within a few hours."}
          </p>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-6 grid lg:grid-cols-12 gap-10">
        {/* FORM */}
        <form onSubmit={submit} className="lg:col-span-7 rounded-2xl border border-[#E2E8F0] bg-white p-8">
          <p className="label-eyebrow">{IT ? "Scrivici un messaggio" : "Brief your concierge"}</p>
          <h2 className="mt-2 font-serif text-2xl text-navy">{IT ? "Come possiamo aiutarti?" : "How can we help?"}</h2>

          <div className="mt-7 grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink">{IT ? "Nome e cognome" : "Full name"}</Label>
              <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink">Email</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink">{IT ? "Telefono / WhatsApp" : "Phone / WhatsApp"}</Label>
              <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink">{IT ? "Oggetto" : "Subject"}</Label>
              <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 h-12" placeholder={IT ? "Es. Vacanza in Sardegna ad agosto" : "e.g. Sardinia in August"} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-ink">{IT ? "La tua richiesta" : "Your request"}</Label>
              <Textarea
                data-testid="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={IT
                  ? "Racconta chi siete, dove vorreste andare, che tipo di esperienza cercate. Più dettagli ci dai, più precisa sarà la nostra risposta."
                  : "Tell us about your party, the regions you have in mind and the kind of experience you'd like on board. The more we know, the more precisely we can curate your proposal."}
                className="mt-1"
              />
            </div>
          </div>

          <Button data-testid="contact-submit" type="submit" disabled={sending} className="mt-7 bg-navy text-white hover:bg-[#06101f] h-12 px-7 rounded-md">
            <Send className="h-4 w-4 mr-2" /> {sending ? (IT ? "Invio…" : "Sending…") : (IT ? "Invia messaggio" : "Send to concierge")}
          </Button>
          <p className="mt-3 text-xs text-ink">{IT ? "Risposta entro 24 ore. I tuoi dati restano riservati." : "Personal reply within 24 hours. Your details remain strictly confidential."}</p>
        </form>

        {/* SIDE INFO */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="rounded-2xl bg-sand border border-[#E5E1D2] p-7">
            <p className="label-eyebrow">{IT ? "Canali diretti" : "Direct channels"}</p>
            <ul className="mt-5 space-y-4">
              <li className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center"><Mail className="h-4 w-4 text-navy" /></span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink">Email</p>
                  <a href="mailto:info@seamatch.pro" data-testid="contact-email-link" className="font-medium text-navy underline-offset-4 hover:underline">info@seamatch.pro</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center"><MessageCircle className="h-4 w-4 text-turquoise" /></span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink">WhatsApp</p>
                  <p className="font-medium text-navy">+39 02 0000 0000</p>
                  <p className="text-xs text-ink mt-0.5">{IT ? "Comodo, veloce, sempre disponibile." : "Discreet and always within reach."}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center"><MapPin className="h-4 w-4 text-gold" /></span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink">{IT ? "Operiamo nel" : "We curate holidays in"}</p>
                  <p className="font-medium text-navy">{IT ? "Mediterraneo · Caraibi · Seychelles · Thailandia" : "The Mediterranean — Italy, Greece, Croatia, France and beyond"}</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-navy text-white p-7">
            <Anchor className="h-5 w-5 text-gold" />
            <p className="mt-4 font-serif text-2xl leading-tight">
              {IT
                ? "Preferisci che siamo noi a contattarti?"
                : "Prefer that we reach out first?"}
            </p>
            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              {IT
                ? "Compila il questionario guidato e ti scriviamo entro 24 ore con le proposte più adatte. Nessuna registrazione, nessun obbligo."
                : "Share a short, guided brief with your concierge and we will return with a hand-built proposal within 24 hours. No account, no obligation."}
            </p>
            <a
              href={localizePath("/search")}
              data-testid="contact-to-search"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:opacity-80"
            >
              {IT ? "Avvia la richiesta guidata" : "Brief my concierge"} →
            </a>
          </div>
        </aside>
      </section>
    </div>
  );
}
